import { StyleProps, useCallbackRef } from '@chakra-ui/react';
import { useState } from 'react';

export const DEFAULT_BLUR_STYLES = {
  content: '""',
  background: 'linear-gradient(0deg, #111527 0%, rgba(17, 21, 39, 0.00) 100%)',
  width: '100%',
  bottom: 0,
  left: 0,
  height: '50px',
  position: 'absolute',
};

const DEFAULT_BLUR_STYLES_HORIZONTAL = {
  content: '""',
  width: '50px',
  height: '100%',
  position: 'absolute' as StyleProps['position'],
  pointerEvents: 'none' as StyleProps['pointerEvents'],
  top: 0,
};

export const DEFAULT_BLUR_STYLES_RIGHT = {
  ...DEFAULT_BLUR_STYLES_HORIZONTAL,
  background:
    'linear-gradient(270deg, #111527 0%, rgba(17, 21, 39, 0.00) 100%)',
  right: 0,
};

export const DEFAULT_BLUR_STYLES_LEFT = {
  ...DEFAULT_BLUR_STYLES_HORIZONTAL,
  background: 'linear-gradient(90deg, #111527 0%, rgba(17, 21, 39, 0.00) 100%)',
  left: 0,
};

export enum ScrollOrientation {
  VERTICAL = 'VERTICAL',
  HORIZONTAL = 'HORIZONTAL',
}

export const useShowBlurOnScrollable = (
  orientation: ScrollOrientation = ScrollOrientation.VERTICAL
) => {
  // for vertical scroll only
  const [showBlur, setShowBlur] = useState(false);
  // for horizontal scroll only
  const [showLeftBlur, setShowLeftBlur] = useState(false);
  const [showRightBlur, setShowRightBlur] = useState(false);

  // Add 1 to the scrollLeft to account for weird half-pixel issue
  const getShouldShowBlurVertical = (element: Element) => {
    const isScrolledToBottom =
      element.scrollHeight - element.clientHeight <= element.scrollTop + 1;

    const isOverflowing =
      (element?.scrollHeight || 0) > (element?.clientHeight || 0);

    return !isScrolledToBottom && isOverflowing;
  };

  const getShouldShowBlurRight = (element: Element) => {
    // Add 1 to the scrollLeft to account for weird half-pixel issue
    const isScrolledToRight =
      element.scrollWidth - element.clientWidth <= element.scrollLeft + 1;

    const isOverflowing =
      (element?.scrollWidth || 0) > (element?.clientWidth || 0);

    return !isScrolledToRight && isOverflowing;
  };

  const getShouldShowBlurLeft = (element: Element) => {
    const isScrolledToLeft = element.scrollLeft <= 1;
    const isOverflowing =
      (element?.scrollWidth || 0) > (element?.clientWidth || 0);

    return !isScrolledToLeft && isOverflowing;
  };

  const handleBlurOnScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (orientation === ScrollOrientation.VERTICAL) {
      setShowBlur(getShouldShowBlurVertical(event.currentTarget));
    } else {
      setShowLeftBlur(getShouldShowBlurLeft(event.currentTarget));
      setShowRightBlur(getShouldShowBlurRight(event.currentTarget));
    }
  };

  const setInitialBlurRef = useCallbackRef(
    (ref: Element | null) => {
      if (ref) {
        if (orientation === ScrollOrientation.VERTICAL) {
          setShowBlur(getShouldShowBlurVertical(ref));
        } else {
          setShowLeftBlur(getShouldShowBlurLeft(ref));
          setShowRightBlur(getShouldShowBlurRight(ref));
        }
      }
    },
    [
      getShouldShowBlurVertical,
      getShouldShowBlurLeft,
      getShouldShowBlurRight,
      setShowBlur,
      setShowLeftBlur,
      setShowRightBlur,
    ]
  );

  return {
    showBlur,
    showLeftBlur,
    showRightBlur,
    handleBlurOnScroll,
    setInitialBlurRef,
  };
};
